import React, { useContext, useEffect, useState } from 'react';
import $ from 'jquery';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Box, Button, Paper, Stack, TextField } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import Toast from '../../../../components/Toast';
import { options } from '../../../../helpers/toastOptions';
import { parseCpfCnpj, parsePhone } from '../../../../helpers/parseValues';
import { parseStringWithRegex } from '../../../../helpers/parseString';
import { useAuth } from '../../../../hooks/Auth';
import { useIntegration } from '../../../../hooks/Integration';
import { useModal } from '../../../../hooks/Modal';
import { StepButtons } from '../StepButtons';
import { Title } from './styled';
import { DefaultCollectContext } from '../..';

export const CollectData = () => {
  const [document, setDocument] = useState('');
  const [ZipPostalCode, setZipPostalCode] = useState('');
  // const [zipSearchedByDocument, setZipSearchedByDocument] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [hasInvalidZipCode, setHasInvalidZipCode] = useState(false);
  const [zipSearchedByDocument, setZipSearchedByDocument] = useState('');

  const { user, token } = useAuth();
  const { searchUser, getAddressByZipPostalCode } = useIntegration();
  const { closeModal } = useModal();

  const maxSM = useMediaQuery('(max-width:768px)');
  const maxMD = useMediaQuery('(max-width:900px)');

  const {
    handleNextStep,
    requester,
    addressList,
    selectedAddressIndex,
    setCollectSenderData,
    collectSenderData,
    openChangeCollectAddressModal,
    disableSenderName,
    setDisableSenderName,
    disableSenderStreetAddress,
    setDisableSenderStreetAddress,
    disableSenderSuburb,
    setDisableSenderSuburb,
    isToValidateSenderDocument,
    setIsToValidateSenderDocument,
    setEnableDeclaration,
    setDataToAddCollectAddressModal,
    openAddCollectAddressModal,
    isToValidateSenderAddressNumber,
    setIsToValidateSenderAddressNumber,
    openLoadingModal
  } = useContext(DefaultCollectContext);

  const collectSenderSchema = Yup.object().shape({
    document: Yup.string()
      .required('CNPJ/CPF obrigatório')
      .trim('CNPJ/CPF obrigatório')
      .test('validadeDocument', 'CNPJ/CPF inválido', (value) => {
        if (isToValidateSenderDocument) {
          return (
            typeof value === 'string' &&
            [11, 14].includes(parseCpfCnpj(value)?.length)
          );
        }

        return true;
      }),
    name: Yup.string()
      .required('Razão Social / Nome obrigatório')
      .trim('Razão Social / Nome obrigatório'),
    zipPostalCode: Yup.string()
      .required('CEP obrigatório')
      .trim('CEP obrigatório')
      .test('validateZipPostalCode', 'CEP inválido', (value) => {
        const regex = /^\d{8}$/;
        return (
          value && value.replace('-', '').match(regex) && !hasInvalidZipCode
        );
      }),
    streetAddress: Yup.string()
      .required('Endereço obrigatório')
      .trim('Endereço obrigatório'),
    streetAddressNumber: Yup.string()
      .required('Número obrigatório')
      .trim('Número obrigatório')
      .test('validateStreetAddressNumber', 'Número inválido', (value) => {
        if (!isToValidateSenderAddressNumber) {
          return true;
        }

        return value && value !== '0';
      }),
    additionalInformation: Yup.string().nullable(),
    suburb: Yup.string()
      .required('Bairro obrigatório')
      .trim('Bairro obrigatório'),
    city: Yup.string()
      .required('Cidade obrigatória')
      .trim('Cidade obrigatória'),
    state: Yup.string()
      .required('Estado obrigatório')
      .trim('Estado obrigatório'),
    contactName: Yup.string()
      .required('Nome obrigatório')
      .trim('Nome obrigatório'),
    email: Yup.string()
      .required('E-mail obrigatório')
      .trim('E-mail obrigatório')
      .matches(/^([\w.-]+)@([\w-]+)((\.(\w){2,25})+)$/, 'E-mail inválido'),
    phoneNumber: Yup.string()
      .required('Telefone obrigatório')
      .trim('Telefone obrigatório')
      .test('validatePhoneNumber', 'Telefone inválido', (value) => {
        return parsePhone(value).length >= 10;
      }),
    extension: Yup.string()
  });

  const {
    handleSubmit,
    control,
    setValue,
    setError,
    clearErrors,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(collectSenderSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange'
  });

  const getDocumentInfo = async (value) => {
    setIsToValidateSenderDocument(true);

    if (value) {
      setEnableDeclaration(false);

      try {
        const response = await searchUser({
          searchData: { document: parseCpfCnpj(value) }
        });

        if (response?.length > 0) {
          const userSearched = response[0];

          if (userSearched?.document) {
            const {
              name,
              zipPostalCode,
              streetAddress,
              streetAddressNumber,
              addressComplement,
              suburb,
              city,
              state,
              declaration
            } = userSearched;

            if (declaration === 'S') {
              setEnableDeclaration(true);
            }

            setValue('name', name.toUpperCase());
            setValue('zipPostalCode', zipPostalCode);
            setZipSearchedByDocument(zipPostalCode);

            const newStringAddress = parseStringWithRegex({
              string: String(streetAddress).trim(),
              regex: /^[aA-zZ0-9\s]+$/
            });
            setValue('streetAddress', newStringAddress.toUpperCase());
            setValue('streetAddressNumber', streetAddressNumber);
            // eslint-disable-next-line prettier/prettier
            if (addressComplement) {
              const newStringAddressComplement = parseStringWithRegex({
                string: String(addressComplement).trim(),
                regex: /^[aA-zZ0-9\s]+$/
              });
              setValue('additionalInformation', newStringAddressComplement);
            } else {
              setValue('additionalInformation', '');
            }
            // addressComplement
            //   ? setValue('additionalInformation', addressComplement)
            //   : setValue('additionalInformation', '');

            if (suburb) {
              const newStringSuburb = parseStringWithRegex({
                string: String(suburb).trim(),
                regex: /^[aA-zZ\s]+$/
              });

              // setSenderSuburb(newStringSuburb);
              setValue('suburb', newStringSuburb);
            } else {
              setDisableSenderSuburb(false);
              setValue('suburb', '');
              // setSenderSuburb('');
            }

            setValue('city', city.toUpperCase());
            setValue('state', state.toUpperCase());

            setHasInvalidZipCode(false);
            setDisableSenderName(true);
            setDisableSenderStreetAddress(true);
            // setDisableSenderSuburb(true);

            clearErrors([
              'document',
              'name',
              'zipPostalCode',
              'streetAddress',
              'streetAddressNumber',
              'additionalInformation',
              'suburb',
              'city',
              'state'
            ]);

            setZipPostalCode(zipPostalCode);
            setIsToValidateSenderDocument(false);
            setIsToValidateSenderAddressNumber(true);
            // setZipSearchedByDocument(zipPostalCode);
          }
        } else {
          setDisableSenderName(false);

          toast(
            <Toast
              title="Atenção!"
              content="Usuário não foi localizado com o documento informado."
              type="error"
            />,
            options
          );
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
      }
    } else {
      setDisableSenderName(false);
      setValue('name', '');

      if (!ZipPostalCode) {
        setValue('streetAddress', '');
        setValue('streetAddressNumber', '');
        setValue('additionalInformation', '');
        setValue('suburb', '');
        setValue('city', '');
        setValue('state', '');
      }
    }
  };

  const searchAddressByZipPostalCode = async (value) => {
    const regex = /^\d{8}$/;

    if (value === zipSearchedByDocument) {
      return;
    }

    if (value.replace('-', '').match(regex)) {
      // if (value.replace('-', '') === zipSearchedByDocument) {
      //   return;
      // }

      try {
        const data = await getAddressByZipPostalCode({
          zipPostalCode: value.replace('-', ''),
          token
        });

        if (data) {
          const clear = ['zipPostalCode'];

          setHasInvalidZipCode(false);

          if (data.streetAddress) {
            const newStringAddress = parseStringWithRegex({
              string: String(data.streetAddress).trim(),
              regex: /^[aA-zZ0-9\s]+$/
            });

            if (newStringAddress) {
              // setRequesterAddress(newStringAddress?.toUpperCase());
              setValue('streetAddress', newStringAddress.toUpperCase());
              clear.push('streetAddress');
              setDisableSenderStreetAddress(true);
            } else {
              // setRequesterAddress('');
              setValue('streetAddress', '');
              setDisableSenderStreetAddress(false);
            }
          } else {
            setValue('streetAddress', '');
            setDisableSenderStreetAddress(false);
          }

          if (data.suburb) {
            const newStringSuburb = parseStringWithRegex({
              string: String(data.suburb).trim(),
              regex: /^[aA-zZ\s]+$/
            });

            if (newStringSuburb) {
              // setRequesterDistrict(newStringSuburb?.toUpperCase());
              setValue('suburb', newStringSuburb?.toUpperCase());
              clear.push('suburb');
              setDisableSenderSuburb(true);
            } else {
              // setRequesterDistrict('');
              setValue('suburb', '');
              setDisableSenderSuburb(false);
            }
          } else {
            setValue('suburb', '');
            setDisableSenderSuburb(false);
          }

          if (data.city) {
            setValue('city', data.city.toUpperCase());
            clear.push('city');
          } else {
            setValue('city', '');
          }

          if (data.state) {
            setValue('state', data.state.toUpperCase());
            clear.push('state');
          } else {
            setValue('state', '');
          }

          setValue('streetAddressNumber', '');
          setValue('additionalInformation', '');

          clearErrors(clear);
        } else {
          setError('zipPostalCode', {
            message: 'CEP inválido'
          });
          setHasInvalidZipCode(true);
          setDisableSenderStreetAddress(true);
          setDisableSenderSuburb(true);
          setValue('streetAddress', '');
          setValue('streetAddressNumber', '');
          setValue('additionalInformation', '');
          setValue('suburb', '');
          setValue('city', '');
          setValue('state', '');

          toast(
            <Toast
              title="Atenção!"
              content="O CEP informado não foi localizado."
              type="error"
            />,
            options
          );
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
      }
    } else {
      setDisableSenderStreetAddress(true);
      setDisableSenderSuburb(true);
      setValue('streetAddress', '');
      setValue('streetAddressNumber', '');
      setValue('additionalInformation', '');
      setValue('suburb', '');
      setValue('city', '');
      setValue('state', '');
    }
  };

  const onSenderSubmit = async () => {
    const hasInvalidAddressSelected =
      !addressList[selectedAddressIndex].name ||
      !addressList[selectedAddressIndex].document ||
      !addressList[selectedAddressIndex].zipPostalCode ||
      !addressList[selectedAddressIndex].streetAddress ||
      !addressList[selectedAddressIndex].suburb ||
      !addressList[selectedAddressIndex].city ||
      !addressList[selectedAddressIndex].state ||
      !addressList[selectedAddressIndex].contactName ||
      !addressList[selectedAddressIndex].contactNumber ||
      !addressList[selectedAddressIndex].contactEmail;

    if (hasInvalidAddressSelected) {
      toast(
        <Toast
          title="Atenção!"
          content="Selecione um endereço que contenha todas informações obrigatórias."
          type="error"
        />,
        options
      );

      return;
    }

    try {
      openLoadingModal();

      const getAddressResponse = await getAddressByZipPostalCode({
        zipPostalCode: parseCpfCnpj(
          addressList[selectedAddressIndex].zipPostalCode
        )
      });

      if (
        !getAddressResponse ||
        (isToValidateSenderAddressNumber &&
          (!addressList[selectedAddressIndex].streetAddressNumber ||
            addressList[selectedAddressIndex].streetAddressNumber === '0'))
      ) {
        toast(
          <Toast
            title="Atenção!"
            content="Atualizar os dados dos campos destacados em vermelho."
            type="error"
          />,
          options
        );

        const newStringAddress = parseStringWithRegex({
          string: String(
            addressList[selectedAddressIndex].streetAddress
          ).trim(),
          regex: /^[aA-zZ0-9\s]+$/
        });

        setDataToAddCollectAddressModal({
          document: addressList[selectedAddressIndex].document
            ? parseCpfCnpj(addressList[selectedAddressIndex].document)
            : user.document,
          name: addressList[selectedAddressIndex].name || user.name,
          zipPostalCode: addressList[selectedAddressIndex].zipPostalCode
            ? parseCpfCnpj(addressList[selectedAddressIndex].zipPostalCode)
            : '',
          streetAddress:
            addressList[selectedAddressIndex].streetAddress || newStringAddress,
          streetAddressNumber:
            addressList[selectedAddressIndex].streetAddressNumber || '',
          additionalInformation:
            addressList[selectedAddressIndex].additionalInformation || '',
          suburb: addressList[selectedAddressIndex].suburb || newStringAddress,
          city: addressList[selectedAddressIndex].city || '',
          state: addressList[selectedAddressIndex].state || '',
          contactName: addressList[selectedAddressIndex].contactName || '',
          contactEmail: addressList[selectedAddressIndex].contactEmail || '',
          contactNumber: addressList[selectedAddressIndex].contactNumber
            ? parsePhone(addressList[selectedAddressIndex].contactNumber)
            : '',
          contactExtension:
            addressList[selectedAddressIndex].contactExtension || '',
          hasInvalidZipCode: !getAddressResponse,
          hasInvalidStreetAddressNumber:
            !addressList[selectedAddressIndex].streetAddressNumber ||
            addressList[selectedAddressIndex].streetAddressNumber === '0'
        });

        openAddCollectAddressModal();

        return;
      }

      setEnableDeclaration(false);

      const response = await searchUser({
        searchData: {
          document: parseCpfCnpj(addressList[selectedAddressIndex].document)
        }
      });

      if (response?.length > 0) {
        const userSearched = response[0];

        if (userSearched?.document) {
          const { declaration } = userSearched;

          if (declaration === 'S') {
            setEnableDeclaration(true);
          }
        }
      }

      closeModal();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
      closeModal();
    }

    setCollectSenderData({
      document: addressList[selectedAddressIndex].document
        ? parseCpfCnpj(addressList[selectedAddressIndex].document)
        : user.document,
      name: addressList[selectedAddressIndex].name || user.name,
      address: {
        zipPostalCode: parseCpfCnpj(
          addressList[selectedAddressIndex].zipPostalCode
        ),
        streetAddress: addressList[selectedAddressIndex].streetAddress,
        streetAddressNumber:
          addressList[selectedAddressIndex].streetAddressNumber,
        suburb: addressList[selectedAddressIndex].suburb,
        city: addressList[selectedAddressIndex].city,
        state: addressList[selectedAddressIndex].state,
        country: addressList[selectedAddressIndex].country || 'BRASIL',
        additionalInformation:
          addressList[selectedAddressIndex].additionalInformation || ''
      },
      contactList: [
        {
          name: addressList[selectedAddressIndex].contactName || '',
          phoneNumber: addressList[selectedAddressIndex].contactNumber
            ? parsePhone(addressList[selectedAddressIndex].contactNumber)
            : '',
          extension: addressList[selectedAddressIndex].contactExtension || '',
          email: addressList[selectedAddressIndex].contactEmail || ''
        }
      ]
    });

    handleNextStep();
  };

  const onNextStep = async (data) => {
    openLoadingModal();

    try {
      const getAddressResponse = await getAddressByZipPostalCode({
        zipPostalCode: parseCpfCnpj(data.zipPostalCode)
      });

      if (!getAddressResponse) {
        setError('zipPostalCode', {
          message: 'CEP inválido'
        });

        closeModal();

        return;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }

    setCollectSenderData({
      document: parseCpfCnpj(data.document),
      name: data.name,
      address: {
        zipPostalCode: parseCpfCnpj(data.zipPostalCode),
        streetAddress: parseCpfCnpj(data.streetAddress),
        streetAddressNumber: data.streetAddressNumber,
        suburb: data.suburb,
        city: data.city,
        state: data.state,
        country: data.country || 'BRASIL',
        additionalInformation: data.additionalInformation || ''
      },
      contactList: [
        {
          name: data.contactName,
          phoneNumber: parsePhone(data.phoneNumber),
          extension: data.extension || '',
          email: data.email
        }
      ]
    });

    closeModal();

    handleNextStep();
  };

  useEffect(() => {
    const cpfCnpjOptions = {
      onKeyPress(cpf, _ev, _el, op) {
        const masks = ['000.000.000-000', '00.000.000/0000-00'];
        $('#document').mask(cpf.length > 14 ? masks[1] : masks[0], op);
      }
    };

    $('#document').length > 11
      ? $('#document').mask('00.000.000/0000-00', cpfCnpjOptions)
      : $('#document').mask('000.000.000-00#', cpfCnpjOptions);
  }, [document]);

  useEffect(() => {
    $('#zipPostalCode').mask('00000-000');
  }, [ZipPostalCode]);

  useEffect(() => {
    $('#phoneNumber').mask('(00) 0000-00009');
  }, [contactNumber]);

  useEffect(() => {
    if (requester === 'recipient' && collectSenderData.document) {
      setValue('document', collectSenderData.document);
      setValue('name', collectSenderData.name.toUpperCase());
      setValue('zipPostalCode', collectSenderData.address.zipPostalCode);

      if (collectSenderData?.address?.streetAddress) {
        const newStringAddress = parseStringWithRegex({
          string: String(collectSenderData?.address?.streetAddress).trim(),
          regex: /^[aA-zZ0-9\s]+$/
        });
        setValue('streetAddress', newStringAddress);
      } else {
        setValue('streetAddress', '');
        setDisableSenderStreetAddress(false);
      }
      // setValue(
      //   'streetAddress',
      //   collectSenderData.address.streetAddress.toUpperCase()
      // );
      setValue(
        'streetAddressNumber',
        collectSenderData.address.streetAddressNumber
      );

      if (collectSenderData?.address?.additionalInformation) {
        const newStringAddressComplement = parseStringWithRegex({
          // eslint-disable-next-line prettier/prettier
          string: String(collectSenderData?.address?.additionalInformation).trim(),
          regex: /^[aA-zZ0-9\s]+$/
        });
        setValue(
          'additionalInformation',
          newStringAddressComplement.toUpperCase()
        );
      } else {
        setValue('additionalInformation', '');
      }
      // collectSenderData.address.additionalInformation ? setValue('additionalInformation', collectSenderData.address.additionalInformation.toUpperCase()) : setValue('additionalInformation', '');

      if (collectSenderData?.address?.suburb) {
        const newStringSuburb = parseStringWithRegex({
          string: String(collectSenderData?.address?.suburb).trim(),
          regex: /^[aA-zZ\s]+$/
        });

        if (newStringSuburb) {
          setValue('suburb', newStringSuburb?.toUpperCase());
          setDisableSenderSuburb(true);
        } else {
          setValue('suburb', '');
          setDisableSenderSuburb(false);
        }
      } else {
        setValue('suburb', '');
        setDisableSenderSuburb(false);
      }
      // setValue('suburb', collectSenderData.address.suburb.toUpperCase());
      setValue('city', collectSenderData.address.city.toUpperCase());
      setValue('state', collectSenderData.address.state.toUpperCase());
      setValue(
        'contactName',
        collectSenderData.contactList[0].name.toUpperCase()
      );
      setValue('email', collectSenderData.contactList[0].email.toLowerCase());
      setValue('phoneNumber', collectSenderData.contactList[0].phoneNumber);
      setValue('extension', collectSenderData.contactList[0].extension || '');

      setDocument(collectSenderData.document);
      setZipPostalCode(collectSenderData.address.zipPostalCode);
      setContactNumber(collectSenderData.contactList[0].phoneNumber);
    }

    $('#userAddressZipPostalCode').mask('00000-000');

    $('#userAddressPhoneNumber').mask('(00) 0000-00000');
  }, []);

  return (
    <>
      <Paper
        elevation={2}
        style={{
          position: 'relative',
          marginTop: 16,
          padding: '20px 16px 16px'
        }}
      >
        <Stack
          direction={maxSM ? 'column' : 'row'}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: maxSM ? 16 : 0
          }}
        >
          <Title>Dados da Coleta:</Title>

          {requester === 'sender' && (
            <Button
              type="button"
              variant="contained"
              data-testid="handleAddCollectAddressButton"
              onClick={openChangeCollectAddressModal}
              style={{ marginTop: maxSM ? 16 : 0 }}
            >
              Alterar / Adicionar
            </Button>
          )}
        </Stack>
        {requester === 'recipient' && (
          <Stack spacing={2} style={{ marginTop: 24 }}>
            <Stack spacing={2}>
              <Stack direction={maxMD ? 'column' : 'row'} spacing={2}>
                <Controller
                  name="document"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      label="CNPJ / CPF *"
                      id="document"
                      data-testid="senderDocumentInput"
                      style={{ minWidth: 240 }}
                      {...field}
                      value={(field.value = document)}
                      onChange={(e) => {
                        setIsToValidateSenderDocument(true);
                        setDocument(e.target.value);
                        field.onChange(e.target.value);
                      }}
                      onBlur={(e) => {
                        getDocumentInfo(e.target.value);
                        setDocument(e.target.value);
                        field.onBlur(field.onChange(e.target.value));
                      }}
                      error={!!errors.document}
                      helperText={errors.document?.message}
                    />
                  )}
                />

                <Controller
                  name="name"
                  defaultValue=""
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Razão Social/Nome *"
                      data-testid="senderNameInput"
                      style={{ width: '100%' }}
                      {...field}
                      disabled={disableSenderName}
                      onChange={(e) => {
                        const regex = /^[aA-zZ0-9\s]+$/;
                        if (
                          !e.target.value.match(regex) &&
                          e.target.value !== ''
                        ) {
                          return;
                        }
                        field.onChange(e.target.value.toUpperCase());
                      }}
                      onBlur={(e) => {
                        field.onBlur(
                          field.onChange(e.target.value.toUpperCase())
                        );
                      }}
                      inputProps={{ maxLength: 256 }}
                      error={!!errors.name}
                      helperText={errors.name?.message}
                    />
                  )}
                />
              </Stack>

              <Stack direction={maxSM ? 'column' : 'row'} spacing={2}>
                <Controller
                  name="zipPostalCode"
                  defaultValue=""
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="CEP *"
                      id="zipPostalCode"
                      data-testid="senderZipPostalCodeInput"
                      style={{ width: !maxMD && 240 }}
                      inputProps={{ maxLength: 9 }}
                      {...field}
                      value={(field.value = ZipPostalCode)}
                      onChange={(e) => {
                        setHasInvalidZipCode(true);
                        setZipPostalCode(e.target.value);
                        field.onChange(e.target.value);
                      }}
                      onBlur={(e) => {
                        const { value } = e.target;
                        searchAddressByZipPostalCode(value);

                        setZipPostalCode(value);
                        field.onBlur(field.onChange(value));
                      }}
                      error={!!errors.zipPostalCode}
                      helperText={errors.zipPostalCode?.message}
                    />
                  )}
                />

                <Controller
                  name="streetAddress"
                  defaultValue=""
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Endereço *"
                      data-testid="senderStreetAddressInput"
                      style={{ width: maxMD && '100%', flex: !maxMD && '1' }}
                      {...field}
                      disabled={
                        disableSenderStreetAddress || disableSenderSuburb
                      }
                      onChange={(e) => {
                        const regex = /^[aA-zZ0-9\s]+$/;
                        if (
                          !e.target.value.match(regex) &&
                          e.target.value !== ''
                        ) {
                          return;
                        }
                        field.onChange(e.target.value.toUpperCase());
                      }}
                      onBlur={(e) => {
                        field.onBlur(
                          field.onChange(e.target.value.toUpperCase())
                        );
                      }}
                      inputProps={{ maxLength: 256 }}
                      error={!!errors.streetAddress}
                      helperText={errors.streetAddress?.message}
                    />
                  )}
                />

                <Controller
                  name="streetAddressNumber"
                  defaultValue=""
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Número *"
                      data-testid="senderStreetAddressNumberInput"
                      style={{ width: maxSM ? '100%' : 96, minWidth: 96 }}
                      {...field}
                      onChange={(e) => {
                        const regex = /^[0-9]+$/;
                        if (
                          !e.target.value.match(regex) &&
                          e.target.value !== ''
                        ) {
                          return;
                        }

                        setIsToValidateSenderAddressNumber(false);
                        field.onChange(e.target.value);
                      }}
                      onBlur={(e) => {
                        field.onBlur(field.onChange(e.target.value));
                      }}
                      inputProps={{ maxLength: 50 }}
                      error={!!errors.streetAddressNumber}
                      helperText={errors.streetAddressNumber?.message}
                    />
                  )}
                />
              </Stack>

              <Stack direction={maxMD ? 'column' : 'row'} spacing={2}>
                <Stack
                  direction={maxSM ? 'column' : 'row'}
                  spacing={2}
                  style={{ width: maxMD ? '100%' : '55%' }}
                >
                  <Controller
                    name="additionalInformation"
                    defaultValue=""
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label="Complemento"
                        data-testid="senderAdditionalInformationInput"
                        style={{ width: !maxMD && 240 }}
                        fullWidth={maxMD}
                        {...field}
                        onChange={(e) => {
                          const regex = /^[aA-zZ0-9\s]+$/;
                          if (
                            !e.target.value.match(regex) &&
                            e.target.value !== ''
                          ) {
                            return;
                          }
                          field.onChange(e.target.value.toUpperCase());
                        }}
                        onBlur={(e) => {
                          field.onBlur(
                            field.onChange(e.target.value.toUpperCase())
                          );
                        }}
                        inputProps={{ maxLength: 256 }}
                        error={!!errors.additionalInformation}
                        helperText={errors.additionalInformation?.message}
                      />
                    )}
                  />

                  <Controller
                    name="suburb"
                    defaultValue=""
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label="Bairro *"
                        data-testid="senderSuburbInput"
                        style={{ flex: !maxMD && '1' }}
                        fullWidth={maxMD}
                        {...field}
                        disabled={disableSenderSuburb}
                        onChange={(e) => {
                          const regex = /^[aA-zZ\s]+$/;
                          if (
                            !e.target.value.match(regex) &&
                            e.target.value !== ''
                          ) {
                            return;
                          }
                          field.onChange(e.target.value.toUpperCase());
                        }}
                        onBlur={(e) => {
                          field.onBlur(
                            field.onChange(e.target.value.toUpperCase())
                          );
                        }}
                        inputProps={{ maxLength: 50 }}
                        error={!!errors.suburb}
                        helperText={errors.suburb?.message}
                      />
                    )}
                  />
                </Stack>

                <Stack
                  direction="row"
                  spacing={2}
                  style={{ width: maxMD ? '100%' : '45%' }}
                >
                  <Controller
                    name="city"
                    defaultValue=""
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label="Cidade *"
                        data-testid="senderCityInput"
                        style={{ flex: !maxMD && '1' }}
                        fullWidth={maxMD}
                        {...field}
                        disabled
                        onChange={(e) => {
                          const regex = /^[aA-zZ0-9\s]+$/;
                          if (
                            !e.target.value.match(regex) &&
                            e.target.value !== ''
                          ) {
                            return;
                          }
                          field.onChange(e.target.value.toUpperCase());
                        }}
                        onBlur={(e) => {
                          field.onBlur(
                            field.onChange(e.target.value.toUpperCase())
                          );
                        }}
                        inputProps={{ maxLength: 50 }}
                        error={!!errors.city}
                        helperText={errors.city?.message}
                      />
                    )}
                  />

                  <Controller
                    name="state"
                    defaultValue=""
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label="UF *"
                        data-testid="senderStateInput"
                        style={{ width: 96, minWidth: 96 }}
                        {...field}
                        disabled
                        onChange={(e) => {
                          const regex = /^[aA-zZ\s]+$/;
                          if (
                            !e.target.value.match(regex) &&
                            e.target.value !== ''
                          ) {
                            return;
                          }
                          field.onChange(e.target.value.toUpperCase());
                        }}
                        onBlur={(e) => {
                          field.onBlur(
                            field.onChange(e.target.value.toUpperCase())
                          );
                        }}
                        inputProps={{ maxLength: 2 }}
                        error={!!errors.state}
                        helperText={errors.state?.message}
                      />
                    )}
                  />
                </Stack>
              </Stack>
            </Stack>

            <strong>Contato:</strong>
            <Stack
              direction={maxMD ? 'column' : 'row'}
              spacing={2}
              style={{ width: '100%' }}
            >
              <Controller
                name="contactName"
                defaultValue=""
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Nome *"
                    data-testid="senderContactNameInput"
                    fullWidth
                    {...field}
                    onChange={(e) => {
                      const { value } = e.target;
                      const regex = /^[aA-zZ\s]+$/;

                      if (!value.match(regex) && value !== '') {
                        return;
                      }

                      field.onChange(value.toUpperCase());
                    }}
                    onBlur={(e) => {
                      field.onBlur(
                        field.onChange(e.target.value.toUpperCase())
                      );
                    }}
                    inputProps={{ maxLength: 256 }}
                    error={!!errors.contactName}
                    helperText={errors.contactName?.message}
                  />
                )}
              />

              <Controller
                name="email"
                defaultValue=""
                control={control}
                render={({ field }) => (
                  <TextField
                    label="E-mail *"
                    data-testid="senderContactEmailInput"
                    fullWidth
                    {...field}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                    }}
                    onBlur={(e) => {
                      field.onBlur(
                        field.onChange(e.target.value.toLowerCase())
                      );
                    }}
                    inputProps={{ maxLength: 256 }}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                  />
                )}
              />

              <Stack direction="row" spacing={2} style={{ width: '100%' }}>
                <Controller
                  name="phoneNumber"
                  defaultValue=""
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Telefone *"
                      id="phoneNumber"
                      data-testid="senderContactPhoneInput"
                      fullWidth
                      {...field}
                      onChange={(e) => {
                        setContactNumber(e.target.value);
                        field.onChange(e.target.value);
                      }}
                      onBlur={(e) => {
                        if (e.target.value.length > 14) {
                          $('#phoneNumber').mask('(00) 00000-0009');
                        } else {
                          $('#phoneNumber').mask('(00) 0000-00009');
                        }

                        setContactNumber(e.target.value);
                        field.onBlur(field.onChange(e.target.value));
                      }}
                      inputProps={{ maxLength: 50 }}
                      error={!!errors.phoneNumber}
                      helperText={errors.phoneNumber?.message}
                    />
                  )}
                />

                <Controller
                  name="extension"
                  defaultValue=""
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Ramal"
                      data-testid="senderContactExtensionInput"
                      style={{ width: 96, minWidth: 96 }}
                      {...field}
                      onChange={(e) => field.onChange(e.target.value)}
                      onBlur={(e) => {
                        field.onBlur(field.onChange(e.target.value));
                      }}
                      inputProps={{ maxLength: 50 }}
                      error={!!errors.extension}
                      helperText={errors.extension?.message}
                    />
                  )}
                />
              </Stack>
            </Stack>
          </Stack>
        )}

        {requester === 'sender' && (
          <Box>
            <p>
              {addressList[selectedAddressIndex].name || user.name} -{' '}
              {addressList[selectedAddressIndex].document || user.document}
            </p>
            <p>
              CEP:{' '}
              <span id="userAddressZipPostalCode">
                {addressList[selectedAddressIndex].zipPostalCode}
              </span>
              , {addressList[selectedAddressIndex].streetAddress}
              {addressList[selectedAddressIndex].streetAddressNumber &&
                `, ${addressList[selectedAddressIndex].streetAddressNumber}`}
              {addressList[selectedAddressIndex].additionalInformation &&
                `, ${addressList[selectedAddressIndex].additionalInformation}`}
              , {addressList[selectedAddressIndex].suburb},{' '}
              {addressList[selectedAddressIndex].city}-
              {addressList[selectedAddressIndex].state}
            </p>
            <div>
              {addressList[selectedAddressIndex].contactName !== '' && (
                <div>
                  CONTATO: {addressList[selectedAddressIndex].contactName}
                  {addressList[selectedAddressIndex].contactNumber !== '' && (
                    <>, {addressList[selectedAddressIndex]?.contactNumber}</>
                  )}
                  {addressList[selectedAddressIndex].contactExtension &&
                    `, RAMAL ${addressList[selectedAddressIndex].contactExtension}`}
                  , {addressList[selectedAddressIndex].contactEmail}
                </div>
              )}
            </div>
          </Box>
        )}
      </Paper>

      <StepButtons
        nextFunction={
          requester === 'sender' ? onSenderSubmit : handleSubmit(onNextStep)
        }
      />
    </>
  );
};
